// Shared helpers for resolving a product's alias/urlAlias and its "product family" (all
// versions sharing the same productName) to the current latest non-deprecated version.
// Consolidates alias-matching logic that used to be duplicated in ApiProductHOC.js and
// ApiDocumentationHOC.js, and replaces the old family-alias flow that used to hit a
// dead legacy Drupal JSON:API endpoint. See devportal-manager-cms-feature memory.

export const normalizeAlias = (value) => {
  if (!value) return null;

  return String(value)
    .replace(/^\/apiProduct\//, "")
    .replace(/^\/+|\/+$/g, "")
    .toLowerCase();
};

// Same slug rule assets-api's createUrlAlias/createFriendlyUrl use server-side, so a bare
// family URL built from a product's name lines up with how a blank urlAlias auto-generates.
export const normalizeProductName = (value) => {
  if (!value) return null;

  return String(value)
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
};

// A product's alias as the :apiProductId route param. Migrated urlAliases already carry the
// "/apiProduct/" prefix, so passing them to getRouteUrl as-is produced
// "/apiProduct//apiProduct/<alias>" (404). Case is kept, unlike normalizeAlias.
export const aliasToRouteParam = (value) => {
  if (!value) return null;

  return String(value)
    .replace(/^\/?apiProduct\//i, "")
    .replace(/^\/+|\/+$/g, "");
};

// Trailing version in a migrated alias: "-v1.2", "-1.0.0", "-v2", "-vv24", "-v13-5689".
const VERSION_SUFFIX = /-v{0,2}\d+(\.\d+)*(-\d+)?$/i;

// The product's version-less alias ("Bill-Payments-v3.0" -> "Bill-Payments"): the
// permanent link that always shows the family's current version.
export const familyAlias = (value) => {
  const bare = aliasToRouteParam(value);
  if (!bare) return null;

  return bare.replace(VERSION_SUFFIX, "") || bare;
};

// Link to a product page: the version-less family link for current products, the exact
// versioned alias for deprecated ones (they keep their own page).
export const productLink = (alias, deprecated) => {
  const bare = aliasToRouteParam(alias);
  if (!bare) return null;

  return `/apiProduct/${deprecated ? bare : familyAlias(bare)}`;
};

const productAlias = (product) =>
  product?.alias || product?.urlAlias || product?.UrlAlias || null;

const productId = (product) =>
  product?.uuid || product?.id || product?.Id || null;

// The product's own version ("v3.0"). Raw catalogue items carry it as productVersion -
// their `version` is a revision counter - while parsed items expose it as `version`.
const productVersionOf = (product) =>
  product?.productVersion ||
  product?.ProductVersion ||
  product?.version ||
  product?.Version;

export const findProductByAlias = (products, identifier) => {
  const target = normalizeAlias(identifier);
  if (!target || !Array.isArray(products)) return null;

  return (
    products.find((product) => {
      const candidates = [product?.alias, product?.urlAlias, product?.UrlAlias]
        .filter(Boolean)
        .map(normalizeAlias);

      return candidates.includes(target);
    }) || null
  );
};

// Compares version strings that aren't uniformly formatted across migrated products
// ("v3.0", "3.0", "1.1", even "v2.7 " with trailing whitespace) - splits into numeric
// segments and compares them positionally rather than relying on string/lexicographic sort.
export const compareVersions = (a, b) => {
  const segments = (value) =>
    String(value || "")
      .trim()
      .replace(/^v/i, "")
      .split(".")
      .map((part) => parseInt(part, 10) || 0);

  const segA = segments(a);
  const segB = segments(b);
  const length = Math.max(segA.length, segB.length);

  for (let i = 0; i < length; i++) {
    const diff = (segA[i] || 0) - (segB[i] || 0);
    if (diff !== 0) return diff;
  }
  return 0;
};

// Given the full product list and either a product name or a family-slug identifier, finds
// the latest non-deprecated version of that product family. Used both for the bare
// family-URL fallback (a slug that doesn't match any exact urlAlias) and for the
// "go to new version" link shown on a deprecated product's own page.
export const resolveLatestActiveVersionForFamily = (products, nameOrSlug) => {
  if (!Array.isArray(products) || !nameOrSlug) return null;

  const targetName = String(nameOrSlug).trim().toLowerCase();
  const targetSlug = normalizeProductName(nameOrSlug);
  // Only a version-less identifier may match by family alias - a versioned one
  // ("Bill-Payments-v2.8") names that exact version and must not jump to the latest.
  const bareTarget = (aliasToRouteParam(nameOrSlug) || "").toLowerCase();
  const familyTarget = (familyAlias(nameOrSlug) || "").toLowerCase();
  const targetFamilyAlias = familyTarget === bareTarget ? familyTarget : "";

  const candidates = products.filter((product) => {
    const productName = (product?.productName || product?.title || "")
      .trim()
      .toLowerCase();
    if (productName === targetName && productName) return true;
    if (productName && normalizeProductName(productName) === targetSlug) {
      return true;
    }
    // Version-less alias whose slug differs from the product name
    // (e.g. "Commercial-Tokenization-API" for "Commercial Tokenization")
    const alias = productAlias(product);
    return (
      !!alias &&
      !!targetFamilyAlias &&
      (familyAlias(alias) || "").toLowerCase() === targetFamilyAlias
    );
  });

  const active = candidates.filter(
    (product) => product?.deprecated !== true && product?.Deprecated !== true
  );
  const pool = active.length > 0 ? active : candidates;
  if (pool.length === 0) return null;

  return pool.reduce((latest, current) =>
    compareVersions(productVersionOf(current), productVersionOf(latest)) > 0
      ? current
      : latest
  );
};

// A versioned identifier ("Bill-Payments-v3.0") matched by family + version number rather
// than by stored alias: environments store the same version under different aliases
// (prod keeps Drupal's "/apiProduct/Bill-Payments" for v3.0, QA "/apiProduct/Bill-Payments-v3.0").
// Only current products are in the list; old versions are resolved by their own alias.
export const findProductByFamilyAndVersion = (products, identifier) => {
  if (!Array.isArray(products)) return null;

  const bare = aliasToRouteParam(identifier);
  const suffix = bare && bare.match(VERSION_SUFFIX);
  if (!suffix) return null;

  const family = familyAlias(bare).toLowerCase();
  const version = suffix[0].replace(/^-v{0,2}/i, "");
  // "-v13-5689"-style suffixes carry a node id, not a comparable version
  if (/-/.test(version)) return null;

  return (
    products.find((product) => {
      if (product?.deprecated === true || product?.Deprecated === true) {
        return false;
      }
      const alias = productAlias(product);
      const name = product?.productName || product?.title || "";
      const sameFamily =
        (!!alias && (familyAlias(alias) || "").toLowerCase() === family) ||
        (!!name && normalizeProductName(name) === family);
      const productVersion = productVersionOf(product);

      return (
        sameFamily &&
        !!productVersion &&
        compareVersions(productVersion, version) === 0
      );
    }) || null
  );
};

// For a current product opened by its own versioned alias: the version-less family link to
// settle the address bar on, or null when the URL should stay as it is (already the family
// link, or the family link would resolve to a different product).
export const familyRedirectFor = (products, product, identifier) => {
  const alias = productAlias(product);
  if (!alias || product?.deprecated === true || product?.Deprecated === true) {
    return null;
  }

  const family = familyAlias(alias);
  if (
    !family ||
    family.toLowerCase() === (aliasToRouteParam(identifier) || "").toLowerCase()
  ) {
    return null;
  }

  const latest = resolveLatestActiveVersionForFamily(products, family);
  return latest && productId(latest) === productId(product)
    ? `/apiProduct/${family}`
    : null;
};
